import { toStringValue, toTimestamp } from '@/features/charting-next/adapters/chartingAdapterUtils';

export const CHARTING_RESULT_SORT_ORIGINAL = 'original';

const sortCollator = new Intl.Collator('es', {
    numeric: true,
    sensitivity: 'base'
});

const getRawValue = (stock, ...keys) => {
    const raw = stock?.raw || {};

    for (const key of keys) {
        const value = raw[key];

        if (value !== null && value !== undefined && value !== '') {
            return value;
        }
    }

    return '';
};

const firstValue = (...values) => {
    for (const value of values) {
        if (value !== null && value !== undefined && value !== '') {
            return value;
        }
    }

    return '';
};

const parseSortNumber = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    const normalizedText = toStringValue(value);

    if (!normalizedText || normalizedText === '-') {
        return null;
    }

    let numericText = normalizedText.replace(/\s/g, '').replace(/[^\d,.\-+]/g, '');

    if (!numericText) {
        return null;
    }

    const lastCommaIndex = numericText.lastIndexOf(',');
    const lastDotIndex = numericText.lastIndexOf('.');

    if (lastCommaIndex !== -1 && lastDotIndex !== -1) {
        numericText = lastCommaIndex > lastDotIndex ? numericText.replace(/\./g, '').replace(',', '.') : numericText.replace(/,/g, '');
    } else if (lastCommaIndex !== -1) {
        numericText = numericText.replace(',', '.');
    }

    const parsed = Number(numericText);

    return Number.isFinite(parsed) ? parsed : null;
};

const parseSortDate = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const normalizedText = toStringValue(value);
    const dayFirstMatch = normalizedText.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);

    if (dayFirstMatch) {
        const [, day, month, year] = dayFirstMatch;
        const normalizedYear = year.length === 2 ? `20${year}` : year;
        const timestamp = Date.UTC(Number(normalizedYear), Number(month) - 1, Number(day));

        return Number.isFinite(timestamp) ? timestamp : null;
    }

    return toTimestamp(value);
};

const SORT_DEFINITIONS = [
    {
        field: 'ticker',
        labels: ['Ticker A-Z', 'Ticker Z-A'],
        type: 'text',
        resolveValue: (stock) => stock?.ticker
    },
    {
        field: 'entry_type',
        labels: ['Tipo de entrada A-Z', 'Tipo de entrada Z-A'],
        type: 'text',
        resolveValue: (stock) => firstValue(stock?.entryTypeLabel, getRawValue(stock, 'entry_type_name', 'tipus_entrada', 'entry_type'))
    },
    {
        field: 'instrument_number',
        labels: ['Acciones ASC', 'Acciones DESC'],
        type: 'number',
        resolveValue: (stock) => firstValue(stock?.instrumentNumberValue, stock?.instrumentNumberLabel, getRawValue(stock, 'instrument_number'))
    },
    {
        field: 'in_price',
        labels: ['Entrada ASC', 'Entrada DESC'],
        type: 'number',
        resolveValue: (stock) => firstValue(stock?.entryPriceValue, stock?.entryPriceLabel, getRawValue(stock, 'real_entry', 'in_price', 'entry_price'))
    },
    {
        field: 'out_price',
        labels: ['Stop ASC', 'Stop DESC'],
        type: 'number',
        resolveValue: (stock) => firstValue(stock?.stopPriceValue, stock?.stopPriceLabel, getRawValue(stock, 'out_price', 'stop_price'))
    },
    {
        field: 'support_price',
        labels: ['Soporte ASC', 'Soporte DESC'],
        type: 'number',
        resolveValue: (stock) => firstValue(stock?.supportPriceValue, stock?.supportPriceLabel, getRawValue(stock, 'soport', 'support_price'))
    },
    {
        field: 'support_date',
        labels: ['Fecha soporte ASC', 'Fecha soporte DESC'],
        type: 'date',
        resolveValue: (stock) => firstValue(getRawValue(stock, 'data_soport', 'support_date'), stock?.supportDateLabel, stock?.supportDateFormatted)
    },
    {
        field: 'current_price',
        labels: ['Precio ASC', 'Precio DESC'],
        type: 'number',
        resolveValue: (stock) => firstValue(stock?.currentPriceValue, stock?.priceLabel, getRawValue(stock, 'valor_actual', 'current_price', 'price'))
    },
    {
        field: 'in_currency',
        labels: ['Divisa ASC', 'Divisa DESC'],
        type: 'text',
        resolveValue: (stock) => firstValue(stock?.currencyQuoteLabel, stock?.currency, getRawValue(stock, 'cotitzacio_divisa', 'divisa'))
    },
    {
        field: 'tthh_profit',
        labels: ['Rentabilidad ASC', 'Rentabilidad DESC'],
        type: 'number',
        resolveValue: (stock) => firstValue(stock?.profitability, stock?.profitabilityLabel, getRawValue(stock, 'tae_lt', 'profitability_interval'))
    },
    {
        field: 'entry_distance',
        labels: ['Dist. entrada ASC', 'Dist. entrada DESC'],
        type: 'number',
        resolveValue: (stock) => firstValue(stock?.entryDistanceValue, stock?.entryDistanceLabel, getRawValue(stock, 'entry_distance'))
    },
    {
        field: 'resistance',
        labels: ['Resistencia ASC', 'Resistencia DESC'],
        type: 'number',
        resolveValue: (stock) => firstValue(stock?.resistancePriceValue, getRawValue(stock, 'resistance', 'r'))
    },
    {
        field: 'anual_var',
        labels: ['Var. anual ASC', 'Var. anual DESC'],
        type: 'number',
        resolveValue: (stock) => getRawValue(stock, 'tae', 'anual_var')
    },
    {
        field: 'wave_number',
        labels: ['Onda ASC', 'Onda DESC'],
        type: 'number',
        resolveValue: (stock) => firstValue(stock?.waveNumberLabel, getRawValue(stock, 'num_ona_actual', 'wave_number'))
    },
    {
        field: 'next_entry_lt',
        labels: ['Sig. LT ASC', 'Sig. LT DESC'],
        type: 'number',
        resolveValue: (stock) => getRawValue(stock, 'entrada_seg', 'next_entry_lt')
    },
    {
        field: 'first_trading_date',
        labels: ['Primera fecha ASC', 'Primera fecha DESC'],
        type: 'date',
        resolveValue: (stock) => firstValue(getRawValue(stock, 'fecha_primera_barra', 'first_trading_date'), stock?.firstBarDateLabel, stock?.firstBarDateFormatted)
    },
    {
        field: 'risk_reward',
        labels: ['Inv/Riesgo ASC', 'Inv/Riesgo DESC'],
        type: 'number',
        resolveValue: (stock) => firstValue(stock?.riskRewardValue, stock?.riskRewardLabel, getRawValue(stock, 'risk_reward'))
    },
    {
        field: 'user_platinum_compatibility',
        labels: ['Compat. ASC', 'Compat. DESC'],
        type: 'number',
        resolveValue: (stock) => firstValue(stock?.userCompatibility, stock?.userCompatibilityLabel, getRawValue(stock, 'user_platinum_compatibility'))
    },
    {
        field: 'open_fee',
        labels: ['Comis. ent. ASC', 'Comis. ent. DESC'],
        type: 'number',
        resolveValue: (stock) => getRawValue(stock, 'open_fee')
    },
    {
        field: 'benefit',
        labels: ['Beneficio ASC', 'Beneficio DESC'],
        type: 'number',
        resolveValue: (stock) => getRawValue(stock, 'benefit')
    },
    {
        field: 'insured_benefit',
        labels: ['Benef. aseg. ASC', 'Benef. aseg. DESC'],
        type: 'number',
        resolveValue: (stock) => getRawValue(stock, 'insured_benefit')
    },
    {
        field: 'in_date',
        labels: ['F. entrada ASC', 'F. entrada DESC'],
        type: 'date',
        resolveValue: (stock) => getRawValue(stock, 'in_date')
    },
    {
        field: 'favorite_date',
        labels: ['Fecha añadido ASC', 'Fecha añadido DESC'],
        type: 'date',
        entryTypes: new Set(['favorites']),
        resolveValue: (stock) => getRawValue(stock, 'favorite_date')
    }
];

const SORT_DEFINITIONS_BY_FIELD = new Map(SORT_DEFINITIONS.map((definition) => [definition.field, definition]));

const normalizeDirection = (direction) => {
    const normalizedDirection = toStringValue(direction).toLowerCase();

    return normalizedDirection === 'desc' ? 'desc' : 'asc';
};

const buildSortOption = (definition, direction) => ({
    value: `${definition.field}:${direction}`,
    field: definition.field,
    direction,
    label: direction === 'asc' ? definition.labels[0] : definition.labels[1]
});

const resolveComparableValue = (definition, stock) => {
    const value = definition.resolveValue(stock);

    if (definition.type === 'number') {
        return parseSortNumber(value);
    }

    if (definition.type === 'date') {
        return parseSortDate(value);
    }

    const text = toStringValue(value);

    return text && text !== '-' ? text : null;
};

const compareValues = (left, right, type) => {
    if (type === 'number' || type === 'date') {
        return left === right ? 0 : left > right ? 1 : -1;
    }

    return sortCollator.compare(left, right);
};

export function buildChartingResultSortOptions({ entry } = {}) {
    const entryType = toStringValue(entry?.type);
    const options = [
        {
            value: CHARTING_RESULT_SORT_ORIGINAL,
            label: 'Orden original'
        }
    ];

    SORT_DEFINITIONS.forEach((definition) => {
        if (definition.entryTypes && !definition.entryTypes.has(entryType)) {
            return;
        }

        options.push(buildSortOption(definition, 'asc'));
        options.push(buildSortOption(definition, 'desc'));
    });

    return options;
}

export function normalizeChartingResultSortValue(value) {
    const normalizedValue = toStringValue(value);

    if (!normalizedValue || normalizedValue === CHARTING_RESULT_SORT_ORIGINAL) {
        return CHARTING_RESULT_SORT_ORIGINAL;
    }

    const [field, direction] = normalizedValue.split(':');

    if (!SORT_DEFINITIONS_BY_FIELD.has(field)) {
        return CHARTING_RESULT_SORT_ORIGINAL;
    }

    return `${field}:${normalizeDirection(direction)}`;
}

export function resolveChartingResultSortValue(value, options = []) {
    const normalizedValue = normalizeChartingResultSortValue(value);

    return options.some((option) => option.value === normalizedValue) ? normalizedValue : CHARTING_RESULT_SORT_ORIGINAL;
}

export function sortChartingResults(items = [], sortValue = CHARTING_RESULT_SORT_ORIGINAL) {
    const normalizedValue = normalizeChartingResultSortValue(sortValue);

    if (normalizedValue === CHARTING_RESULT_SORT_ORIGINAL) {
        return items;
    }

    const [field, direction] = normalizedValue.split(':');
    const definition = SORT_DEFINITIONS_BY_FIELD.get(field);

    if (!definition) {
        return items;
    }

    return [...items]
        .map((item, index) => ({
            item,
            index,
            value: resolveComparableValue(definition, item)
        }))
        .sort((left, right) => {
            const leftIsEmpty = left.value === null;
            const rightIsEmpty = right.value === null;

            if (leftIsEmpty || rightIsEmpty) {
                if (leftIsEmpty && rightIsEmpty) {
                    return sortCollator.compare(left.item?.ticker || '', right.item?.ticker || '') || left.index - right.index;
                }

                return leftIsEmpty ? 1 : -1;
            }

            const sortResult = compareValues(left.value, right.value, definition.type);
            const directedSortResult = direction === 'desc' ? -sortResult : sortResult;

            if (directedSortResult !== 0) {
                return directedSortResult;
            }

            return sortCollator.compare(left.item?.ticker || '', right.item?.ticker || '') || left.index - right.index;
        })
        .map(({ item }) => item);
}
